import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUser, FiClock, FiAlertCircle } from 'react-icons/fi';
import auditLogService from '../../services/auditLogService';
import './AuditLogView.css';

const AuditLogView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['auditLog', id],
    queryFn: () => auditLogService.getAuditLogById(id)
  });

  const auditLog = data?.data;

  const getActionBadgeClass = (action) => {
    const classes = {
      CREATE: 'badge-success',
      UPDATE: 'badge-info',
      DELETE: 'badge-danger',
      LOGIN: 'badge-primary',
      LOGOUT: 'badge-secondary',
      APPROVE: 'badge-success',
      REJECT: 'badge-danger'
    };
    return classes[action] || 'badge-secondary';
  };

  const getSeverityBadgeClass = (severity) => {
    const classes = {
      LOW: 'badge-info',
      MEDIUM: 'badge-warning',
      HIGH: 'badge-danger',
      CRITICAL: 'badge-critical'
    };
    return classes[severity] || 'badge-secondary';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      SUCCESS: 'badge-success',
      FAILURE: 'badge-danger',
      PARTIAL: 'badge-warning'
    };
    return classes[status] || 'badge-secondary';
  };

  if (isLoading) return <div className="loading">Loading audit log...</div>;
  if (error) return <div className="error">Error loading audit log: {error.message}</div>;
  if (!auditLog) return <div className="error">Audit log not found</div>;

  return (
    <div className="audit-log-view-container">
      <div className="page-header">
        <button onClick={() => navigate('/app/audit-logs')} className="btn-back">
          <FiArrowLeft /> Back to Audit Logs
        </button>
      </div>

      <div className="audit-log-card">
        <div className="card-header">
          <h1>Audit Log Details</h1>
          <div className="header-badges">
            <span className={`badge ${getActionBadgeClass(auditLog.action)}`}>
              {auditLog.action}
            </span>
            <span className={`badge ${getSeverityBadgeClass(auditLog.severity)}`}>
              {auditLog.severity}
            </span>
            <span className={`badge ${getStatusBadgeClass(auditLog.status)}`}>
              {auditLog.status}
            </span>
          </div>
        </div>

        <div className="card-body">
          {/* Basic Information */}
          <section className="info-section">
            <h2>Basic Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Timestamp</label>
                <p>{new Date(auditLog.created_at).toLocaleString()}</p>
              </div>
              <div className="info-item">
                <label>User</label>
                {auditLog.user ? (
                  <div className="user-details">
                    <p className="user-name">{auditLog.user.first_name} {auditLog.user.last_name}</p>
                    <p className="user-email">{auditLog.user.email}</p>
                  </div>
                ) : (
                  <p className="text-muted">System</p>
                )}
              </div>
              <div className="info-item">
                <label>Entity Type</label>
                <p>{auditLog.entity_type}</p>
              </div>
              <div className="info-item">
                <label>Entity Name</label>
                <p>{auditLog.entity_name || '-'}</p>
              </div>
            </div>
          </section>

          {/* Description */}
          <section className="info-section">
            <h2>Description</h2>
            <div className="description-box">
              <p>{auditLog.description}</p>
            </div>
          </section>

          {/* Changes */}
          {(auditLog.old_values || auditLog.new_values) && (
            <section className="info-section">
              <h2>Changes</h2>
              <div className="changes-grid">
                {auditLog.old_values && (
                  <div className="changes-column">
                    <h3>Old Values</h3>
                    <pre className="json-display">
                      {JSON.stringify(auditLog.old_values, null, 2)}
                    </pre>
                  </div>
                )}
                {auditLog.new_values && (
                  <div className="changes-column">
                    <h3>New Values</h3>
                    <pre className="json-display">
                      {JSON.stringify(auditLog.new_values, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Technical Details */}
          <section className="info-section">
            <h2>Technical Details</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>IP Address</label>
                <p>{auditLog.ip_address || '-'}</p>
              </div>
              <div className="info-item">
                <label>Entity ID</label>
                <p className="monospace">{auditLog.entity_id || '-'}</p>
              </div>
              <div className="info-item full-width">
                <label>User Agent</label>
                <p className="user-agent">{auditLog.user_agent || '-'}</p>
              </div>
            </div>
          </section>

          {/* Error Information */}
          {auditLog.error_message && (
            <section className="info-section error-section">
              <h2><FiAlertCircle /> Error Information</h2>
              <div className="error-box">
                <p>{auditLog.error_message}</p>
              </div>
            </section>
          )}

          {/* Metadata */}
          {auditLog.metadata && (
            <section className="info-section">
              <h2>Additional Metadata</h2>
              <pre className="json-display">
                {JSON.stringify(auditLog.metadata, null, 2)}
              </pre>
            </section>
          )}

          {/* Organization */}
          {auditLog.organization && (
            <section className="info-section">
              <h2>Organization</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Name</label>
                  <p>{auditLog.organization.name}</p>
                </div>
                <div className="info-item">
                  <label>Code</label>
                  <p>{auditLog.organization.code}</p>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogView;


